import { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ArrowRightIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

const pricePerMessage = 0.04;

const included = [
  "SMS dashboard access",
  "Developer API access",
  "Delivery reporting",
  "Basic onboarding support",
  "No long-term commitment",
];

const enterpriseFeatures = [
  "Custom volume pricing",
  "Dedicated account manager",
  "Priority onboarding and support",
  "Custom reporting and SLAs",
  "Integration planning for large teams",
];

const faqs = [
  [
    "How does pay-as-you-go pricing work?",
    "Regular customers pay GHS 0.04 per SMS message. Your total depends on the number of messages you send.",
  ],
  [
    "Do enterprises pay the same price?",
    "Large companies, high-volume senders, and enterprise teams should contact sales for a tailored quote.",
  ],
  [
    "Can I use the API on pay-as-you-go?",
    "Yes. Pay-as-you-go includes access to the dashboard and messaging API so teams can start quickly.",
  ],
  [
    "Are there hidden setup fees?",
    "The regular plan is designed to be simple. Enterprise requirements may vary based on volume, support, and integration needs.",
  ],
];

function Pricing() {
  const [activeTab, setActiveTab] = useState("payg");
  const [messageCount, setMessageCount] = useState(10000);

  const estimatedCost = useMemo(() => messageCount * pricePerMessage, [messageCount]);
  const formattedCost = new Intl.NumberFormat("en-GH", {
    style: "currency",
    currency: "GHS",
    maximumFractionDigits: 2,
  }).format(estimatedCost);

  return (
    <div className="bg-slate-50">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Whistle Pulse Pricing</title>
        <link rel="canonical" href="https://whistlepulse.com/pricing" />
        <meta
          name="description"
          content="Simple Whistle Pulse SMS pricing. Pay as you go at GHS 0.04 per message or contact sales for enterprise and large-volume pricing."
        />
      </Helmet>

      <Navbar />

      <section className="bg-[#071d33] text-white">
        <div className="mx-auto max-w-[85rem] px-4 py-16 text-center sm:px-6 lg:px-8 lg:py-24">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-300">Pricing</p>
          <h1 className="mx-auto mt-5 max-w-4xl text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
            Simple pricing for every message you send.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-blue-50">
            Start with transparent pay-as-you-go SMS pricing. For enterprise volumes, custom support, and large-scale
            rollouts, our team will prepare a tailored quote.
          </p>
          <div className="mx-auto mt-8 inline-flex rounded-full border border-white/10 bg-white/5 p-1">
            {[
              ["payg", "Pay as you go"],
              ["enterprise", "Enterprise"],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
                  activeTab === id ? "bg-white text-[#071d33]" : "text-blue-50 hover:bg-white/10"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto -mt-10 max-w-[85rem] px-4 pb-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/70 sm:p-8">
            {activeTab === "payg" ? (
              <>
                <div className="flex items-start justify-between gap-5">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700">
                      <CreditCardIcon className="h-4 w-4" aria-hidden="true" />
                      Regular customers
                    </div>
                    <h2 className="mt-5 text-3xl font-bold text-slate-950">Pay as you go</h2>
                    <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                      Great for teams that want a low-risk start with reliable SMS delivery, dashboard access, and API
                      integration.
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-500">Per SMS</p>
                    <p className="mt-1 text-4xl font-bold text-slate-950">GHS 0.04</p>
                  </div>
                </div>

                <div className="mt-8 rounded-xl bg-slate-50 p-5">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-semibold text-slate-950">Estimate monthly cost</p>
                      <p className="mt-1 text-sm text-slate-600">Move the slider to preview your SMS spend.</p>
                    </div>
                    <p className="text-2xl font-bold text-blue-700">{formattedCost}</p>
                  </div>
                  <input
                    type="range"
                    min="1000"
                    max="250000"
                    step="1000"
                    value={messageCount}
                    onChange={(event) => setMessageCount(Number(event.target.value))}
                    className="mt-6 w-full accent-blue-600"
                  />
                  <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
                    <span>1,000 SMS</span>
                    <span className="font-semibold text-slate-950">{messageCount.toLocaleString()} SMS</span>
                    <span>250,000 SMS</span>
                  </div>
                </div>

                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {included.map((item) => (
                    <div key={item} className="flex items-start gap-3 rounded-lg border border-slate-200 p-4">
                      <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
                      <p className="text-sm font-semibold text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                  <BuildingOffice2Icon className="h-4 w-4" aria-hidden="true" />
                  Large teams and enterprises
                </div>
                <h2 className="mt-5 text-3xl font-bold text-slate-950">Custom enterprise pricing</h2>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                  Built for banks, fintechs, high-volume senders, agencies, and organizations that need tailored pricing,
                  technical planning, and dedicated support.
                </p>

                <div className="mt-8 rounded-xl bg-[#071d33] p-6 text-white">
                  <p className="text-sm font-semibold text-blue-200">Custom quote</p>
                  <p className="mt-2 text-4xl font-bold">Contact us</p>
                  <p className="mt-3 text-sm leading-6 text-blue-50">
                    Share your expected monthly volume, countries, use cases, and support requirements. We’ll help you
                    find the best setup.
                  </p>
                </div>

                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {enterpriseFeatures.map((item) => (
                    <div key={item} className="flex items-start gap-3 rounded-lg border border-slate-200 p-4">
                      <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
                      <p className="text-sm font-semibold text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-[#071d33] p-6 text-white shadow-xl shadow-slate-200/70 sm:p-8">
            <SparklesIcon className="h-9 w-9 text-blue-300" aria-hidden="true" />
            <h2 className="mt-5 text-2xl font-bold">Pricing built for growth</h2>
            <p className="mt-3 text-sm leading-7 text-blue-50">
              Start small, scale up, and move into custom commercial terms when your messaging volume or support needs
              grow.
            </p>

            <div className="mt-8 grid gap-4">
              {[
                ["No setup friction", "Create an account and start sending with regular pricing."],
                ["Transparent unit cost", "Regular SMS pricing is GHS 0.04 per message."],
                ["Enterprise path", "Large companies can negotiate pricing around volume and support."],
              ].map(([title, description]) => (
                <div key={title} className="rounded-lg border border-white/10 bg-white/5 p-4">
                  <p className="font-semibold">{title}</p>
                  <p className="mt-2 text-sm leading-6 text-blue-100">{description}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-3">
              <a
                href="https://dashboard.whistlepulse.com/auth/register"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-500 px-6 py-3 text-sm font-semibold text-white transition hover:bg-blue-400"
              >
                Start with pay as you go
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="/contact-us"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[85rem] px-4 pb-16 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-3">
          {[
            ["GHS 0.04", "Regular SMS price per message"],
            ["Dashboard + API", "Use Whistle Pulse your way"],
            ["Custom", "Enterprise and high-volume pricing"],
          ].map(([value, label]) => (
            <div key={label} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-3xl font-bold text-slate-950">{value}</p>
              <p className="mt-2 text-sm text-slate-600">{label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[85rem] px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <ShieldCheckIcon className="mx-auto h-9 w-9 text-blue-600" aria-hidden="true" />
            <h2 className="mt-5 text-3xl font-bold text-slate-950">Frequently asked questions</h2>
          </div>
          <div className="mx-auto mt-10 grid max-w-5xl gap-4 md:grid-cols-2">
            {faqs.map(([question, answer]) => (
              <div key={question} className="rounded-lg border border-slate-200 p-6">
                <h3 className="font-bold text-slate-950">{question}</h3>
                <p className="mt-3 text-sm leading-6 text-slate-600">{answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Pricing;
